import React, { useState } from "react";
import { Plus } from "lucide-react";
import { useTopUps, useCreateTopUp, useCancelTopUp, useSupportedConversionPartners } from "../hooks/useTreasury";

const PARTNERS = ["BRAILS", "KORAPAY", "QUIDAX", "CASHRAMP", "BUSHA", "EVERSEND", "RAFIKI", "GREY", "SEPTLY", "RELOADLY", "BLOCKRADAR", "PAYSTACK", "STARTBUTTON"];

const statusStyles = {
  expected: "bg-amber-100 text-amber-800",
  received: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-gray-100 text-gray-500",
};

const empty = { partner: "", currency: "", amount: "", expectedAt: "", note: "" };

// Money finance has sent to a partner that hasn't appeared in its synced
// balance yet. It is shown as "in transit" and is NEVER counted as held -
// it clears itself once the partner's balance rises by about that amount.
function TopUpsPanel() {
  const { data, isLoading } = useTopUps();
  const { mutate: create, isPending } = useCreateTopUp();
  const { mutate: cancel } = useCancelTopUp();
  const { data: supported } = useSupportedConversionPartners();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const topUps = (data || []).filter((t) => t.status === "expected" || t.status === "received").slice(0, 20);
  const partners = Array.from(new Set([...(supported || []), ...PARTNERS]));

  const submit = () => {
    if (!form.partner || !form.currency || !Number(form.amount)) return;
    create(
      {
        partner: form.partner,
        currency: form.currency.toUpperCase(),
        amount: Number(form.amount),
        ...(form.expectedAt && { expectedAt: form.expectedAt }),
        ...(form.note && { note: form.note }),
      },
      { onSuccess: () => setForm(empty) }
    );
  };

  return (
    <div className="mb-4">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex gap-2 items-center text-sm font-medium text-[#013941] hover:underline"
      >
        <Plus className="w-4 h-4" />
        Top-ups in transit ({topUps.filter((t) => t.status === "expected").length}) {open ? "(hide)" : "(show / record)"}
      </button>

      {open && (
        <div className="p-4 mt-2 rounded border border-gray-200 bg-gray-50/50">
          <p className="mb-3 text-xs text-gray-500">
            Record money you have sent to a partner that is not in its balance yet. It is shown as in transit only -
            never counted as held - and clears automatically once the partner's balance rises by about that amount.
          </p>
          <div className="flex flex-wrap gap-3 items-end mb-4">
            <select
              value={form.partner}
              onChange={(e) => setForm({ ...form, partner: e.target.value })}
              className="px-2 py-1.5 text-sm rounded border border-gray-200"
            >
              <option value="">Partner</option>
              {partners.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            <input
              placeholder="Currency"
              value={form.currency}
              onChange={(e) => setForm({ ...form, currency: e.target.value })}
              className="px-2 py-1.5 w-24 text-sm uppercase rounded border border-gray-200"
            />
            <input
              type="number"
              min="0"
              placeholder="Amount"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              className="px-2 py-1.5 w-32 text-sm rounded border border-gray-200"
            />
            <input
              type="date"
              value={form.expectedAt}
              onChange={(e) => setForm({ ...form, expectedAt: e.target.value })}
              className="px-2 py-1.5 text-sm rounded border border-gray-200"
              title="Expected date (optional)"
            />
            <input
              placeholder="Note (optional)"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              className="flex-1 px-2 py-1.5 min-w-[160px] text-sm rounded border border-gray-200"
            />
            <button
              onClick={submit}
              disabled={isPending}
              className="px-4 py-1.5 text-sm font-medium text-white bg-[#013941] rounded-lg hover:bg-[#012126] disabled:opacity-50"
            >
              Record
            </button>
          </div>

          {isLoading ? null : topUps.length === 0 ? (
            <p className="text-sm text-gray-400">Nothing in transit.</p>
          ) : (
            <ul className="space-y-1">
              {topUps.map((t) => (
                <li key={t.uuid} className="flex flex-wrap gap-x-3 items-center text-sm text-gray-700">
                  <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${statusStyles[t.status]}`}>{t.status}</span>
                  <strong>{Number(t.amount).toLocaleString()} {t.currency}</strong>
                  <span>to {t.partner}</span>
                  {t.expectedAt && <span className="text-gray-500">ETA {t.expectedAt}</span>}
                  {t.note && <span className="text-gray-400">{t.note}</span>}
                  {t.status === "expected" && (
                    <button onClick={() => cancel(t.uuid)} className="text-xs text-gray-500 hover:underline">
                      cancel
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default TopUpsPanel;
